'use client';

import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { Link, useLocale } from '@/i18n/routing';
import type { EarnActivity } from '../_apis';
import { getEarnLocalizedText } from '../_localized';
import { getEarnActivityStatus, getEarnActivityTimeRange } from '../_utils';

type ActivityCardProps = {
  activity: EarnActivity;
  actions?: ReactNode;
};

export function ActivityCard({ activity, actions }: ActivityCardProps) {
  const t = useTranslations('earn');
  const locale = useLocale();
  const coverUri = activity.cover_uri;
  const href = activity.id ? `/earn/activities/${activity.id}` : '/earn/activities';
  const title = getEarnLocalizedText(activity.title, locale);
  const status = getEarnActivityStatus(activity);
  const timeRange = getEarnActivityTimeRange(activity);
  const statusText: Record<typeof status, string> = {
    ended: t('activity.ended'),
    in_progress: t('activity.inProgress'),
    not_started: t('activity.notStarted'),
  };
  const statusTextClassName: Record<typeof status, string> = {
    ended: 'text-system-white48',
    in_progress: 'text-[#0086FF]',
    not_started: 'text-system-white',
  };

  return (
    <article className="group flex min-h-[403px] flex-col rounded-[22px] bg-[#1d1e20]/95 p-6 shadow-[0_28px_80px_rgba(0,0,0,0.28)] transition duration-300 hover:bg-[#222326] sm:min-h-0 sm:p-4">
      <div className="relative aspect-[2.58/1] overflow-hidden rounded-[13px] border border-system-white12 bg-[#171b25]">
        {coverUri ? (
          <img
            src={coverUri}
            alt={title || t('activity.coverAlt')}
            className="size-full object-cover transition duration-500 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-[linear-gradient(135deg,#17191f,#08090c_58%,#1f2229)] text-sm text-white/44">
            {title || t('activity.coverFallback')}
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col pt-5">
        <div className="flex items-center justify-between gap-4">
          <Link href={href} className="min-w-0 transition hover:text-white/82">
            <h3 className="line-clamp-1 font-semibold text-[21px] text-white leading-snug sm:text-lg">{title}</h3>
          </Link>
          <span
            className={`shrink-0 rounded-full bg-white/[0.08] px-3 py-1 font-medium text-base leading-6 sm:text-sm ${statusTextClassName[status]}`}
          >
            {statusText[status]}
          </span>
        </div>
        <div className="mt-4 flex items-center gap-3 text-[16px] text-white/88 leading-6 sm:text-sm">
          <span className="size-4 shrink-0 rounded-full border-2 border-[#1396ff]" />
          <span>{timeRange ? `${t('activity.eventTime')} : ${timeRange}` : t('profile.activityTimePending')}</span>
        </div>
        {actions && <div className="mt-auto pt-6">{actions}</div>}
      </div>
    </article>
  );
}
